import { useEffect, useState } from "react";
import { Search, FileSpreadsheet, Eye, ChevronLeft, ChevronRight, Loader2, Star, StickyNote } from "lucide-react";
import { FavoriteButton } from "@/components/portal/CandidateFollowup";
import { FeedbackBadge } from "@/components/portal/ColleagueFeedback";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CENTROS, TITULACION_LIST, shortTit } from "@/lib/demo/seed";
import { fullName, initials, DAY_LABELS, fmt } from "@/lib/demo/utils";
import { api, apiError, openDownload } from "@/lib/api";
import { useI18n } from "@/lib/i18n/i18n";
import { ORIGIN_SHORT } from "@/lib/i18n/dictInteracciones";

const PAGE = 50;
const ORIGIN_TONE = { company_scanned_candidate: "bg-slate-900 text-white", candidate_scanned_company: "bg-upv-light text-upv", post_event_online: "bg-emerald-50 text-emerald-800" };

const FilterSelect = ({ id, value, onChange, placeholder, options }) => {
  const { t } = useI18n();
  return (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-11 w-full rounded-xl sm:w-[190px]" data-testid={`candidates-filter-${id}`}><SelectValue placeholder={t(placeholder)} /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{t("{label}: todos", { label: t(placeholder) })}</SelectItem>
      {options.map(([v, l]) => <SelectItem key={v} value={v}>{t(l)}</SelectItem>)}
    </SelectContent>
  </Select>
  );
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** ZIP of CVs is built server-side in the background; poll until ready, then download natively. */
async function exportZip(filters, t) {
  const id = toast.loading(t("Preparando el ZIP de currículums…"));
  try {
    let { data } = await api.post("/company/exports", filters);
    for (let n = 0; data.status === "processing" && n < 600; n++) {
      toast.loading(t("Generando ZIP… {done} de {total} CV", { done: fmt(data.done || 0), total: fmt(data.total || 0) }), { id });
      await sleep(1500);
      ({ data } = await api.get(`/company/exports/${data.id}`));
    }
    if (data.status !== "ready") throw new Error("zip");
    openDownload(data.url);
    toast.success(t("ZIP listo. Descargando…"), { id });
  } catch (e) {
    toast.error(t(e?.response ? apiError(e).message : "No se ha podido generar el ZIP. Inténtalo de nuevo."), { id });
  }
}

/** Requests a signed export link and lets the browser download it natively. */
export async function requestExport(kind, filters, t) {
  if (kind === "zip") return exportZip(filters, t);
  try {
    const { data } = await api.post("/company/downloads", { kind, ...filters });
    openDownload(data.url);
    toast.success(t("Exportación completada"), { description: t("Archivo Excel (.xlsx) descargado.") });
  } catch (e) {
    toast.error(t(apiError(e).message));
  }
}

export const dayLabel = (i, t) => (DAY_LABELS[i.day] ? t(DAY_LABELS[i.day]) : i.date);

export const CandidatesTable = ({ onOpen, onViewCv, refreshKey }) => {
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [tit, setTit] = useState("all");
  const [centro, setCentro] = useState("all");
  const [day, setDay] = useState("all");
  const [origin, setOrigin] = useState("all");
  const [fav, setFav] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { t } = useI18n();
  const filters = { q: dq || null, titulacion: tit, centro, day, origin, favorite: fav || null };

  useEffect(() => { const id = setTimeout(() => setDq(q), 300); return () => clearTimeout(id); }, [q]);
  useEffect(() => { setPage(1); }, [dq, tit, centro, day, origin, fav]);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/company/candidates", { params: { q: dq || undefined, titulacion: tit, centro, day, origin, favorite: fav || undefined, page, size: PAGE } })
      .then((r) => alive && setData(r.data)).catch((e) => toast.error(t(apiError(e).message))).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [dq, tit, centro, day, origin, fav, page, refreshKey, t]);

  const rows = data?.items || [];
  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;

  return (
    <div className="space-y-5" data-testid="candidates-section">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Buscar candidato")} className="h-11 rounded-xl pl-11" data-testid="candidates-search-input" />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <FilterSelect id="titulacion" value={tit} onChange={setTit} placeholder="Titulación" options={TITULACION_LIST.map((x) => [x, shortTit(x)])} />
          <FilterSelect id="centro" value={centro} onChange={setCentro} placeholder="Centro" options={CENTROS.map((c) => [c, c])} />
          <FilterSelect id="origen" value={origin} onChange={setOrigin} placeholder="Origen" options={Object.entries(ORIGIN_SHORT)} />
          <FilterSelect id="dia" value={day} onChange={setDay} placeholder="Día" options={[["1", "Día 1 · 12 mayo"], ["2", "Día 2 · 13 mayo"], ["0", "Fuera del evento"]]} />
          <button onClick={() => setFav((f) => !f)} aria-pressed={fav} className={`flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl border px-4 text-sm font-semibold transition-colors ${fav ? "border-amber-400 bg-amber-50 text-amber-800" : "border-slate-200 hover:border-slate-900"}`} data-testid="candidates-filter-favorites"><Star className={`h-4 w-4 ${fav ? "fill-amber-400 text-amber-500" : ""}`} /> {t("Favoritos")}</button>
        </div>
        <button onClick={() => requestExport("xlsx", filters, t)} className="flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-emerald-700 px-5 text-sm font-bold tracking-wide text-white transition-colors hover:bg-emerald-800" data-testid="candidates-export-button">
          <FileSpreadsheet className="h-4 w-4" /> {t("EXPORTAR EXCEL")}
        </button>
      </div>
      <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {["", "table|Nombre", "Titulación", "Centro", "Fecha", "Hora", "CV"].map((h) => <TableHead key={h} className="h-12 text-[11px] font-bold uppercase tracking-wider text-slate-500">{t(h)}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const { participant: p, interaction: i } = r;
                return (
                  <TableRow key={i.id} onClick={() => onOpen(r)} className={`cursor-pointer ${i.isNew ? "bg-upv-light/60" : ""}`} data-testid={`candidate-row-${p.id}`}>
                    <TableCell className="w-10 py-4 pr-0"><FavoriteButton pid={p.id} value={i.favorite} onChange={(v) => setData((d) => ({ ...d, items: d.items.map((x) => (x.participant.id === p.id ? { ...x, interaction: { ...x.interaction, favorite: v } } : x)) }))} className="h-8 w-8 rounded-lg hover:bg-amber-50" testId={`candidate-favorite-${p.id}`} /></TableCell>
                    <TableCell className="py-4">
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-bold text-slate-700">{initials(p)}</span>
                        <div className="min-w-0">
                          <p className="flex items-center gap-2 whitespace-nowrap font-semibold text-slate-900">{fullName(p)}{i.isNew && <span className="rounded-full bg-upv px-2 py-0.5 text-[10px] font-bold text-white" data-testid={`candidate-new-badge-${p.id}`}>{t("NUEVO")}</span>}{i.notesCount > 0 && <span className="flex items-center gap-1 text-[11px] font-semibold text-slate-500" title={t("Notas del equipo")} data-testid={`candidate-notes-count-${p.id}`}><StickyNote className="h-3 w-3" />{i.notesCount}</span>}<FeedbackBadge fb={i.colleagueFeedback} testId={`candidate-feedback-${p.id}`} /></p>
                          <p className="truncate text-xs text-slate-500">{p.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{t(shortTit(p.titulacion))}</TableCell>
                    <TableCell className="text-sm font-semibold">{p.centro}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{dayLabel(i, t)}</TableCell>
                    <TableCell className="font-mono text-sm font-bold">{i.time}<span className={`mt-1.5 block w-fit whitespace-nowrap rounded-full px-2 py-0.5 font-sans text-xs font-semibold ${ORIGIN_TONE[i.origin] || "bg-slate-100 text-slate-700"}`} data-testid={`candidate-origin-${p.id}`}>{t(ORIGIN_SHORT[i.origin] || "")}</span></TableCell>
                    <TableCell>
                      <button onClick={(e) => { e.stopPropagation(); onViewCv(p); }} className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-800 transition-colors hover:border-upv hover:text-upv" data-testid={`candidate-view-cv-${p.id}`}>
                        <Eye className="h-3.5 w-3.5" /> {t("VER CV")}
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {data && rows.length === 0 && <TableRow><TableCell colSpan={7} className="py-12 text-center text-sm text-slate-500" data-testid="candidates-empty">{t("No hay candidatos que coincidan con los filtros.")}</TableCell></TableRow>}
              {!data && <TableRow><TableCell colSpan={7} className="py-12 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        <div className="flex flex-col gap-2 border-t border-slate-100 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-slate-400" data-testid="candidates-count">{t("{n} candidatos coinciden · {total} recibidos en total", { n: fmt(data?.total || 0), total: fmt(data?.companyTotal || 0) })}{loading && " …"}</p>
          <div className="flex items-center gap-2 text-xs font-semibold">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 disabled:opacity-40" data-testid="candidates-prev-page"><ChevronLeft className="h-4 w-4" /></button>
            <span data-testid="candidates-page-label">{page} / {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 disabled:opacity-40" data-testid="candidates-next-page"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
};
